import _ from 'lodash';
import * as constants from '../constants';

const nms = [ constants.NAMESPACE, 'store', 'actions' ].join('/');
const log = require('debug')('@mycure/vue-plugins/' + nms);

export const PERSISTED_CURRENT_ORGANIZATION_KEY = 'mc:current-organization';

/// imports and local declarations
/**
 * @typedef {import('./state').default} State
 * @typedef {import('./state').Organization} Organization
 * @typedef {import('./state').OrganizationMembership} OrganizationMembership
 * @typedef {import('./state').Clock} Clock
 * @typedef {import('vuex').ActionContext<State>} ActionContext
 */

/**
 * @typedef {Object} GetCurrentUserMembershipsActionOpts
 * @property {boolean} $forceFetch - use only for refresh buttons
 * @property {number} [limit=20] - limit for pagination
 * @property {number} skip - skip for pagination
 *
 * @callback GetCurrentUserMembershipsAction
 * @param {ActionContext} ctx
 * @param {GetCurrentUserMembershipsActionOpts} opts
 * @returns {Promise<OrganizationMembership[]>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetCurrentUserMembershipsAction}
 */
export const getCurrentUserMemberships = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({ limit: 20 }, opts);
  const user = await sdk.auth().currentUser();
  // signout
  if (!user) {
    log(`#getCurrentUserMemberships: no current user. resetting store...`);
    ctx.commit('setCurrentUserMemberships', []);
    return [];
  }

  // check cache
  if (ctx.state.currentUserMemberships.length && !opts.$forceFetch) {
    log(`#getCurrentUserMemberships: returning cache...`);
    return ctx.state.currentUserMemberships;
  }

  // fetch
  const query = {
    uid: user.uid,
    $populate: {
      facility: {
        service: 'organizations',
        localKey: 'organization',
        $select: [ 'id', 'name', 'picURL' ]
      }
    }
  };

  // pagination
  if (typeof opts.limit === 'number') query.$limit = opts.limit;
  if (typeof opts.skip === 'number') query.$skip = opts.skip;

  log(`#getCurrentUserMemberships: querying memberships...`);

  /** @type {OrganizationMembership[]} */
  const memberships = await sdk.organization.members()
    .find(query)
    .then(({ items }) => items.map(({ $populated, ...i }) => ({
      ...i,
      ...$populated,
      rolesValues: (i.roles || []).map(role => constants.ROLES_MAP[role])
    })));

  ctx.commit('setCurrentUserMemberships', memberships);

  // select active membership
  log(`#getCurrentUserMemberships: selecting active...`);
  await ctx.dispatch('setActiveMembership');

  return memberships;
};

/**
 * @typedef {Object} SetActiveMembershipActionOpts
 * @property {string} membership membership id
 * @property {string} facility facility id
 *
 * @callback SetActiveMembershipAction
 * @param {ActionContext} ctx
 * @param {SetActiveMembershipActionOpts} opts
 * @returns {Promise<OrganizationMembership>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {SetActiveMembershipAction}
 */
export const setActiveMembership = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({
    facility: window.localStorage.getItem(PERSISTED_CURRENT_ORGANIZATION_KEY)
  }, opts);

  log(`#setActiveMembership: setting active membership to`, opts);

  // select current membership
  const memberships = ctx.state.currentUserMemberships;
  const selected =
    // get from cache
    memberships.find(m => m.id === opts.membership || m.facility.id === opts.facility) ||
    // select firs
    memberships[0] ||
    // query from db
    await Promise.resolve().then(async () => {
      log(`querying active membership from db...`);
      const user = await sdk.auth().currentUser();
      const query = {
        uid: user.uid,
        $populate: {
          facility: {
            service: 'organizations',
            localKey: 'organization',
            $select: [ 'id', 'name', 'picURL' ]
          }
        }
      };
      if (opts.membership) query.id = opts.membership;
      else {
        if (!opts.facility) throw new Error(`${nms}: Facility is required`);
        opts.organization = opts.facility;
      }
      const membership = sdk.organization.members().findOne(query);
      if (membership) {
        Object.assign(membership, membership.$populated);
        delete membership.$populated;
        membership.rolesValues = (membership.roles || []).map(role => constants.ROLES_MAP[role]);
      }
      return membership;
    });
  log(`#setActiveMembership: selected active membership`, selected);

  // check selected
  if (!selected) throw new Error(`User doesn't have the specified membership`);

  // cache selected organization
  window.localStorage.setItem(PERSISTED_CURRENT_ORGANIZATION_KEY, selected.facility.id);

  // ensure populated subscription
  if (!selected.facility.subscription) {
    const facility = await sdk.organizations()
      .get(selected.facility.id, {
        query: {
          // TODO: select only the needed fields
          $populate: {
            parent: {
              service: 'organizations',
              method: 'get',
              localKey: 'parent'
            },
            contractsData: {
              service: 'insurance-contracts',
              method: 'find',
              localKey: 'id',
              foreignKey: 'insured',
              $populate: {
                insurerData: {
                  service: 'organizations',
                  method: 'get',
                  localKey: 'insurer'
                }
              }
            },
            subscription: {
              service: 'subscriptions',
              localKey: 'id',
              foreignKey: 'id'
            }
          }
        }
      })
      .then(res => res || selected.facility);
    if (facility.$populated) {
      const { parent, contractsData, subscription } = facility.$populated;
      facility.parent = parent;
      facility.subscription = subscription;
      facility.contractsData = contractsData.map(({ $populated, ...i }) => ({ ...i, $populated }));
      delete facility.$populated;
    }
    selected.facility = facility;
    ctx.commit('updateInCurrentUserMemberships', { items: [ selected ] });
  }

  ctx.commit('setActiveMembership', selected.id);
  return selected;
};

/**
 * @typedef {Object} GetActiveOrganizationOpeningTimesActionOpts
 * @property {boolean} $forceFetch - use only for refresh buttons
 *
 * @callback GetActiveOrganizationOpeningTimesAction
 * @param {ActionContext} ctx
 * @param {GetActiveOrganizationOpeningTimesActionOpts} opts
 * @returns {Promise<{ items: Clock[], total: number }>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetActiveOrganizationOpeningTimesAction}
 */
export const getActiveOrganizationOpeningTimes = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({ limit: 20 }, opts);
  /** @type {Organization} */
  const organization = ctx.getters.activeOrganization;
  if (!organization) throw new Error(`${nms}: Must select an active organization first`);
  const query = {
    type: 'opening',
    organization: organization.id,
    $populate: {
      _opener: {
        service: 'accounts',
        localKey: 'startedBy'
      },
      _closer: {
        service: 'accounts',
        localKey: 'endedBy'
      }
    }
  };

  // pagination
  if (typeof opts.limit === 'number') query.$limit = opts.limit;
  if (typeof opts.skip === 'number') query.$skip = opts.skip;

  if (opts.$sort) query.$sort = opts.$sort;

  const { items, total } = await sdk.clocks().find(query);
  const records = items.map(({ $populated, ...i }) => ({ ...i, ...$populated }));
  ctx.commit('setActiveOrganizationOpeningTimes', records);
  return { items: records, total };
};

/**
 * @typedef {Object} GetActiveOrganizationMembersActionOpts
 * @property {boolean} $forceFetch - use only for refresh buttons
 * @property {number} [limit=20] - limit for pagination
 * @property {number} skip - skip for pagination
 * @property {string} searchString - name of member
 * @property {string[]} roles - Roles to filter
 * @property {string[]} uids - uids to filter
 * @property {string[]} ids - ids to filter
 * @property {string[]} stockRooms - stockRooms to filter
 * @property {boolean} [commitToStore=true] - flag for committing to store
 *
 * @callback GetActiveOrganizationMembersAction
 * @param {ActionContext} ctx
 * @param {GetActiveOrganizationMembersActionOpts} opts
 * @returns {Promise<OrganizationMembership[]>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetActiveOrganizationMembersAction}
 */
export const getActiveOrganizationMembers = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({ limit: 20, commitToStore: true }, opts);

  // check cache
  if (ctx.state.activeOrganizationMembers.length && !opts.$forceFetch) return ctx.state.activeOrganizationMembers;

  /** @type {Organization} */
  const organization = ctx.getters.activeOrganization;
  if (!organization) throw new Error(`${nms}: Must select an active organization first`);
  const query = {
    organization: organization.id
  };

  // pagination
  if (typeof opts.limit === 'number') query.$limit = opts.limit;
  // NOTE: additional checking is added in order to handle queries with 'pagination'
  //       and 'searchString'; if so, 'skip' is applied to 'query.$search'
  if (typeof opts.skip === 'number' && _.isEmpty(opts.searchString)) query.$skip = opts.skip;

  if (!_.isEmpty(opts.roles)) {
    query.roles = {
      $in: opts.roles
    };
  }

  if (!_.isEmpty(opts.uids)) {
    query.uid = {
      $in: _.isArray(opts.uids) ? opts.uids : [opts.uids]
    };
  }

  if (!_.isEmpty(opts.ids)) {
    query.id = {
      $in: _.isArray(opts.ids) ? opts.ids : [opts.ids]
    };
  }

  if (!_.isEmpty(opts.stockRooms)) {
    query.stockRooms = {
      $in: _.isArray(opts.stockRooms) ? opts.stockRooms : [opts.stockRooms]
    };
  }

  if (!_.isEmpty(opts.searchString)) {
    query.$search = {
      limit: opts.limit,
      organization: organization.id,
      text: opts.searchString,
      skip: opts.skip
    };
  }

  const members = await sdk.organization.members().find(query).then(r => r.items.map(i => ({ ...i, facility: organization })));
  if (opts.commitToStore) ctx.commit('setActiveOrganizationMembers', members);
  return members;
};

/**
 * @typedef {Object} OpenActiveOrganizationActionOpts
 *
 * @callback OpenActiveOrganizationAction
 * @param {ActionContext} ctx
 * @param {OpenActiveOrganizationActionOpts} opts
 * @returns {Promise<Clock>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {OpenActiveOrganizationAction}
 */
export const openActiveOrganization = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({ }, opts);
  /** @type {Organization} */
  const organization = ctx.getters.activeOrganization;
  if (!organization) throw new Error(`${nms}: Must select an active organization first`);
  const data = { type: 'opening', organization: organization.id };
  const clock = await sdk.clocks().create(data);
  ctx.commit('updateInActiveOrganizationOpeningTimes', { item: clock, upsert: true });
  return clock;
};

/**
 * @typedef {Object} CloseActiveOrganizationActionOpts
 * @property {boolean} [cleanup=true] run cleanup function before closing
 * - end unfinished encounters
 * - clear all queues
 * - reset queue number
 * @property {boolean} cleanupForce run cleanup function forcefully before closing
 *
 * @callback CloseActiveOrganizationAction
 * @param {ActionContext} ctx
 * @param {CloseActiveOrganizationActionOpts} opts
 * @returns {Promise<Clock>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {CloseActiveOrganizationAction}
 */
export const closeActiveOrganization = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({ cleanup: true }, opts);
  /** @type {Organization} */
  const organization = ctx.getters.activeOrganization;
  if (!organization) throw new Error(`${nms}: Must select an active organization first`);

  log(`#closeActiveOrganization: closing organization`, organization);

  // cleanup
  if (opts.cleanup) {
    log(`#closeActiveOrganization: cleaning up...`);
    // end encounters
    const encountersQuery = { facility: organization.id, finishedAt: null };
    const encounters = await sdk.medical.encounters().find({ ...encountersQuery, $limit: 0 });
    // throw for not forced
    if (encounters.total && !opts.cleanupForce) {
      const error = new Error(`${nms}: Active organization has pending encounters`);
      error.code = 'organization/close-clinic-has-pending-encounters';
      throw error;
    }
    // force end
    if (encounters.total) await sdk.medical.encounters().update(encountersQuery, { finish: true, force: true, save: true });

    // update pending queue items as finished
    const queueItemsQuery = { organization: organization.id, finishedAt: { $exists: false } };
    await sdk.queue.items().update(queueItemsQuery, { finish: true });

    // reset queue numbers
    const countersQuery = {
      type: { $in: ['queue-number', 'organization-queue-number'] },
      organization: organization.id
    };
    await sdk.counters().update(countersQuery, { value: 0 });
  }

  const query = { type: 'opening', organization: organization.id, endAt: null };
  const clock = await sdk.clocks().update(query, { end: true });
  log(`#closeActiveOrganization: closed`, clock);
  if (clock) ctx.commit('updateInActiveOrganizationOpeningTimes', { item: clock, upsert: true });
  return clock;
};

/**
 * TODO: get proper flat schema
 * @typedef {Object} CreateOrganizationActionOpts
 *
 * @callback CreateOrganizationAction
 * @param {ActionContext} ctx
 * @param {CreateOrganizationActionOpts} opts
 * @returns {Promise<Organization>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {CreateOrganizationAction}
 */
export const createOrganization = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  const data = opts;
  if (_.isEmpty(data)) throw new Error(`${nms}: Invalid create op payload`);

  // execute
  const organization = await sdk.organizations().create(data);

  return organization;
};

/**
 * TODO: get proper flat schema
 * @typedef {Object} UpdateOrganizationActionOpts
 * @property {string} id
 *
 * @callback UpdateOrganizationAction
 * @param {ActionContext} ctx
 * @param {UpdateOrganizationActionOpts} opts
 * @returns {Promise<Organization>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {UpdateOrganizationAction}
 */
export const updateOrganization = (store, sdk) => async (ctx, opts) => {
  /** @type {Organization} */
  const organization = ctx.getters.activeOrganization;
  opts = Object.assign({ id: organization && organization.id }, opts);

  const { id, ...data } = opts;
  if (!id) throw new Error(`${nms}: Id required`);
  if (_.isEmpty(data)) throw new Error(`${nms}: Invalid update op payload`);

  // execute
  const updated = await sdk.organizations().update(id, data);
  if (updated) {
    ctx.commit('updateInOrganizations', { items: [ updated ] });
  }

  return updated;
};

/**
 * @typedef {Object} UploadOrganizationPhotoActionOpts
 * @property {string} id
 * @property {string} [field='picURL']
 * @property {string} base64
 * @property {boolean} [save=true]
 *
 * @callback UploadOrganizationPhotoAction
 * @param {ActionContext} ctx
 * @param {UploadOrganizationPhotoActionOpts} opts
 * @returns {Promise<Organization>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {UploadOrganizationPhotoAction}
 */
export const uploadOrganizationPhoto = (store, sdk) => async (ctx, opts) => {
  /** @type {Organization} */
  const organization = ctx.getters.activeOrganization;
  opts = Object.assign({ id: organization && organization.id, field: 'picURL' }, opts);

  const { id, base64 } = opts;
  if (!id) throw new Error(`${nms}: Id required`);
  if (!base64) throw new Error(`${nms}: Base64 data required`);

  // upload
  const uploadTask = sdk.organizations().uploadPic(id, base64);
  const url = await uploadTask.upload();

  // save
  if (opts.save) {
    await ctx.dispatch('updateOrganization', _.set({ id: opts.id }, opts.field, url));
  }

  return url;
};

/**
 * @typedef {Object} GetOrganizationActionOpts
 * @property {string} id
 * @property {boolean} $forceFetch - use only for refresh buttons
 *
 * @callback GetOrganizationAction
 * @param {ActionContext} ctx
 * @param {GetOrganizationActionOpts} opts
 * @returns {Promise<OrganizationMembership[]>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetOrganizationAction}
 */
export const getOrganization = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  if (!opts.id) throw new Error(`${nms}: Id is required`);

  // TODO: check cache

  const organization = await sdk.organizations().get(opts.id);
  if (organization) ctx.commit('updateInOrganizations', { upsert: true, items: [ organization ] });
  return organization;
};

/**
 * @typedef {Object} GetOrganizationsActionOpts
 * @property {boolean} diagnosticCenters TODO: move to diagnostics plugin
 * @property {boolean} $forceFetch - use only for refresh buttons
 *
 * @callback GetOrganizationsAction
 * @param {ActionContext} ctx
 * @param {GetOrganizationsActionOpts} opts
 * @returns {Promise<OrganizationMembership[]>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetOrganizationsAction}
 */
export const getOrganizations = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);

  // TODO: check cache

  /** @type {Organization} */
  const organization = ctx.getters.activeOrganization;
  const query = { };
  if (opts.diagnosticCenters) {
    if (!organization) throw new Error(`${nms}: Must select an active organization first`);
    query.overlords = organization.id;
    query.type = 'diagnostic-center';
  }
  const organizations = await sdk.organizations().find(query).then(r => r.items);
  ctx.commit('updateInOrganizations', { upsert: true, items: organizations });
  return organizations;
};

/**
 * @typedef {Object} SetSelectedOrganizationActionOpts
 * @property {string} id
 * @property {boolean} $forceFetch - use only for refresh buttons
 *
 * @callback SetSelectedOrganizationAction
 * @param {ActionContext} ctx
 * @param {SetSelectedOrganizationActionOpts} opts
 * @returns {Promise<OrganizationMembership[]>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {SetSelectedOrganizationAction}
 */
export const setSelectedOrganization = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({}, opts);
  if (!opts.id) throw new Error(`${nms}: Id is required`);

  const organization = await ctx.dispatch('getOrganization', { id: opts.id });
  if (organization) {
    ctx.commit('setSelectedOrganization', organization);
  }

  return organization;
};

/**
 * @typedef {Object} GetOrganizationFamilyActionOpts
 * @property {string} id
 * @property {number} [limit=20]
 * @property {number} skip
 * @property {string} searchString
 * @property {string[]} $select
 *
 * @callback GetOrganizationFamilyAction
 * @param {ActionContext} ctx
 * @param {GetOrganizationFamilyActionOpts} opts
 * @returns {Promise<Organization[]>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetOrganizationFamilyAction}
 */
export const getOrganizationFamily = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({
    limit: 20,
    $select: ['type', 'name', 'description', 'picURL']
  }, opts);
  if (!opts.id) throw new Error(`${nms}: Id is required`);

  const orgId = opts.id;
  const requiredSelect = ['id'];
  const query = {
    $or: [
      { id: orgId },
      { _ch: orgId },
      { _sb: orgId },
      { parent: orgId }
    ],
    $select: _.union(requiredSelect, opts.$select)
  };

  if (typeof opts.limit === 'number') query.$limit = opts.limit;
  if (typeof opts.skip === 'number') query.$skip = opts.skip;
  if (!_.isEmpty(opts.searchString)) {
    query.name = { $regex: `^${opts.searchString}`, $options: 'i' };
  }

  const result = await sdk.organizations().find(query);
  return result.items;
};

/**
 * NOTE: add limiting of query
 *
 * @typedef {Object} GetOrganizationByIdsActionOpts
 * @property {string[]} ids
 * @property {string} searchString
 * @property {string[]} $select
 *
 * @callback GetOrganizationByIdsAction
 * @param {ActionContext} ctx
 * @param {GetOrganizationByIdsActionOpts} opts
 * @returns {Promise<Organization[]>}
 *
 * @param {import('vuex').Store} store
 * @param {import('@mycure/sdk').CoreService} sdk
 * @returns {GetOrganizationByIdsAction}
 */
export const getOrganizationByIds = (store, sdk) => async (ctx, opts) => {
  opts = Object.assign({
    $select: ['type', 'name', 'description', 'picURL']
  }, opts);
  if (_.isEmpty(opts.ids)) throw new Error(`${nms}: Ids are required`);

  const requiredSelect = ['id', 'name'];
  const query = {
    id: { $in: opts.ids },
    $select: _.union(requiredSelect, opts.$select)
  };

  if (!_.isEmpty(opts.searchString)) {
    query.name = { $regex: `^${opts.searchString}`, $options: 'i' };
  }

  const result = await sdk.organizations().find(query);
  return result.items;
};
